import { Alert, Button, Group, Stack } from '@mantine/core'
import { IconArrowUpDashed, IconDeviceFloppy, IconExclamationCircle } from '@tabler/icons-react'
import { memo, useEffect, useState } from 'react'
import { Link } from 'wouter'

import { useGlobalState } from '@/hooks/useGlobalState'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { castZodFormToContentModule } from '@/utils/contentModuleForm'
import { getFieldDisplayName, validateFieldsList } from '@/utils/forms'
import { getRoutePath, navigateTo } from '@/utils/navigation'
import { remove } from '@/utils/storage'

export const ActionsSection = memo(function ActionsSection(props: CMFormSection) {
  const { uiStore } = useGlobalState()
  const [alertError, setAlertError] = useState<string | null>(null)
  const [alertScrollTarget, setAlertScrollTarget] = useState<Parameters<typeof props.scrollToSection>[0] | null>(null)
  const [alertScrollField, setAlertScrollField] = useState<string | null>(null)

  // Clear validation errors when clearValidationTrigger changes
  useEffect(() => {
    if (props.clearValidationTrigger !== undefined && props.clearValidationTrigger > 0) {
      setAlertError(null)
      setAlertScrollTarget(null)
      setAlertScrollField(null)
    }
  }, [props.clearValidationTrigger])

  async function validateForm() {
    setAlertError(null)
    setAlertScrollTarget(null)
    setAlertScrollField(null)

    const validationResult = props.moduleForm.validate()

    // Required fields for all modules
    // Note: categories (topic) is optional
    const requiredFields: (keyof typeof props.moduleForm.values)[] = props.isSponsorMskCM
      ? ['name', 'shortDescription', 'presentationMode', 'demographic', 'introduction', 'outline', 'deliveryStyle']
      : [
          'name',
          'shortDescription',
          'selectEsk',
          'presentationMode',
          'demographic',
          'introduction',
          'outline',
          'deliveryStyle',
        ]

    const validation = validateFieldsList(props.moduleForm, requiredFields, validationResult.errors)

    if (!validation.isValid) {
      const { firstFailingField, targetSection, scrollTarget } = validation

      setAlertError(
        `The module contains errors or lacks required information. Please check the "${getFieldDisplayName(firstFailingField!)}" field and others for errors.`,
      )
      setAlertScrollField(scrollTarget!)

      // Special handling: if trying to scroll to topicSettings but it's hidden (sponsor MSK CM),
      // fallback to requiredSettings
      const finalSection =
        targetSection === 'topicSettings' && props.isSponsorMskCM ? 'requiredSettings' : targetSection

      setAlertScrollTarget(finalSection as Parameters<typeof props.scrollToSection>[0])

      return { ok: false }
    }

    if (!props.stepsQuery.data?.length) {
      setAlertError('The module can not be submitted without any sections. Please add at least one section.')
      setAlertScrollTarget('contentSteps')
      return { ok: false }
    }

    if (!props.stepsQuery.data?.every(step => step.valid)) {
      setAlertError(
        'One or more sections contain errors or lack required information. Please check the sections for errors.',
      )
      setAlertScrollTarget('contentSteps')
      return { ok: false }
    }

    const cmResources = props.cmResourcesQuery?.data ?? []
    if (!cmResources.every(resource => resource.valid)) {
      setAlertError(
        'The module has one or more supplemental resources with missing information. Please check the resources for errors.',
      )
      setAlertScrollTarget('optionalSettings')
      return { ok: false }
    }

    return { ok: true }
  }

  async function updateCM(opts?: { validate?: boolean }) {
    const { validate = false } = opts || {}

    props.scrollToSection('actions')

    if (validate) {
      const { ok } = await validateForm()

      if (!ok) return
    }

    const values = props.moduleForm.getValues()

    try {
      // First, validate and save FAQs - this will throw if there are validation errors
      await props.saveCMFAQs?.()

      // Only proceed with content module update if FAQ validation passed
      await props.cmUpdateMutation.mutateAsync(
        { ...castZodFormToContentModule(values), task_id: props.taskId! },
        {
          onSettled: () => {
            remove(`@sidekick/forms/cm/${values.taskId}`)
          },
        },
      )
    } catch (error) {
      console.error('Failed to save content module:', error)

      // If it's a FAQ validation error, don't proceed with navigation
      if (error instanceof Error && error.message === 'FAQ_VALIDATION_ERROR') {
        return // Keep the form open, validation errors are already shown
      }

      // For other errors, let the mutation error handling show the appropriate user message
      return
    }
    navigateTo('contentModules')
  }

  const isMutating = props.cmUpdateMutation.isPending

  return (
    <Stack gap="xl">
      {!!alertError && (
        <Alert
          variant="filled"
          color="red"
          radius="md"
          title="Could Not Submit Form"
          icon={<IconExclamationCircle stroke={1.5} />}>
          <Stack align="flex-start">
            {alertError}

            {(!!alertScrollTarget || !!alertScrollField) && (
              <Button
                size="xs"
                variant="filled"
                color="red"
                bg="rgba(255, 255, 255, 0.3)"
                onClick={() => {
                  if (alertScrollField && props.scrollToField) {
                    props.scrollToField(alertScrollField)
                  } else if (alertScrollTarget) {
                    props.scrollToSection(alertScrollTarget)
                  }
                }}
                leftSection={<IconArrowUpDashed stroke={2} />}>
                Scroll to Error
              </Button>
            )}
          </Stack>
        </Alert>
      )}

      <Group gap="xl">
        <Button
          leftSection={<IconDeviceFloppy />}
          onClick={() => {
            updateCM({ validate: true })
            uiStore.setIsNewContentModule(false)
          }}
          loading={isMutating}>
          Save Module
        </Button>

        {!isMutating && (
          <Button
            component={Link}
            to={getRoutePath('contentModules')}
            size="sm"
            variant="light"
            color="gray"
            onClick={() => uiStore.setIsNewContentModule(false)}>
            Close
          </Button>
        )}
      </Group>
    </Stack>
  )
})
